import React, { useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

interface Plan {
  id: number;
  name: string;
  monthly_price?: number | null;
}

interface PlanSelectorProps {
  availablePlans: Plan[];
  selectedPlanIds: number[];
  onSelectionChange: (planIds: number[]) => void;
  isLoading?: boolean;
  maxHeight?: string;
}

export const PlanSelector: React.FC<PlanSelectorProps> = ({
  availablePlans,
  selectedPlanIds,
  onSelectionChange,
  isLoading = false,
  maxHeight = 'max-h-96',
}) => {
  const handlePlanToggle = (planId: number) => {
    const newSelection = selectedPlanIds.includes(planId)
      ? selectedPlanIds.filter((id) => id !== planId)
      : [...selectedPlanIds, planId];
    onSelectionChange(newSelection);
  };

  const selectedPlans = useMemo(
    () => availablePlans.filter((plan) => selectedPlanIds.includes(plan.id)),
    [availablePlans, selectedPlanIds]
  );

  const unselectedPlans = useMemo(
    () => availablePlans.filter((plan) => !selectedPlanIds.includes(plan.id)),
    [availablePlans, selectedPlanIds]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  if (availablePlans.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center">
        <p className="text-sm text-gray-500">No plans available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selected Plans */}
      {selectedPlans.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="mb-2 text-sm font-semibold text-blue-900">Selected Plans</p>
          <div className="space-y-2">
            {selectedPlans.map((plan) => (
              <div key={plan.id} className="flex items-center gap-2">
                <Checkbox
                  id={`selected-plan-${plan.id}`}
                  checked={true}
                  onCheckedChange={() => handlePlanToggle(plan.id)}
                />
                <Label
                  htmlFor={`selected-plan-${plan.id}`}
                  className="flex-1 cursor-pointer font-medium text-blue-900"
                >
                  {plan.name}
                  {plan.monthly_price && (
                    <span className="ml-2 text-xs text-blue-700">
                      ${plan.monthly_price.toFixed(2)}/mo
                    </span>
                  )}
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Plans */}
      {unselectedPlans.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-semibold text-gray-700">Available Plans</p>
          <ScrollArea className={`rounded-lg border p-2 ${maxHeight}`}>
            <div className="space-y-2 pr-4">
              {unselectedPlans.map((plan) => (
                <div key={plan.id} className="flex items-center gap-2 rounded-lg p-2 hover:bg-gray-50">
                  <Checkbox
                    id={`plan-${plan.id}`}
                    checked={false}
                    onCheckedChange={() => handlePlanToggle(plan.id)}
                  />
                  <Label
                    htmlFor={`plan-${plan.id}`}
                    className="flex-1 cursor-pointer text-sm"
                  >
                    {plan.name}
                    {plan.monthly_price && (
                      <span className="ml-2 text-xs text-gray-600">
                        ${plan.monthly_price.toFixed(2)}/mo
                      </span>
                    )}
                  </Label>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Summary */}
      {selectedPlans.length > 0 && (
        <div className="border-t pt-3">
          <Badge variant="outline" className="bg-blue-100 text-blue-800">
            {selectedPlans.length} plan{selectedPlans.length !== 1 ? 's' : ''} selected
          </Badge>
        </div>
      )}
    </div>
  );
};

export default PlanSelector;
